import { z } from "zod";
import { ClaimIdSchema } from "./agency-unlock.js";
import { ReportIdSchema } from "../primitives/ids.js";
import { PublicScanTopIssueV1Schema } from "./public-scan.js";
export { ClaimIdSchema };
export const FIX_KIT_SKU_PILOT = "FIX_KIT_PILOT";
export const FixKitIdSchema = z
    .string()
    .regex(/^fkit_[a-zA-Z0-9_-]+$/)
    .brand();
export const FixKitPrioritySchema = z.enum(["HIGH", "MEDIUM", "LOW"]);
export const FixKitItemV1Schema = z
    .object({
    issueRef: z.string().min(1),
    sourceCheckId: z.string().min(1).optional(),
    issueTitle: z.string().min(1),
    issueDetail: z.string().min(1),
    priority: FixKitPrioritySchema,
    recommendedFix: z.string().min(1),
    rationale: z.string().min(1),
    clientFacingLanguage: z.string().min(1),
    ownershipHint: z.enum(["AGENCY", "CLIENT", "SHARED"]),
})
    .strict();
/** Agency-persisted Fix Kit artifact (stored JSON). */
export const FixKitArtifactV1Schema = z
    .object({
    schemaVersion: z.literal("fix-kit-artifact.v1.0"),
    fixKitId: FixKitIdSchema,
    claimId: ClaimIdSchema,
    publicReportRef: ReportIdSchema,
    sku: z.literal(FIX_KIT_SKU_PILOT),
    generatedAt: z.string().datetime(),
    executiveSummary: z.string().min(1),
    items: z.array(FixKitItemV1Schema).min(1).max(10),
    nextStepProposal: z.string().min(1),
    disclaimers: z.array(z.string().min(1)).min(1),
    sourceScore: z.number().int().min(0).max(100),
    sourceScoreLabel: z.string().min(1),
    topIssuesSnapshot: z.array(PublicScanTopIssueV1Schema).max(5),
})
    .strict();
export const MANUAL_PAYMENT_STATUSES = ["UNPAID", "PAID", "REFUNDED", "VOID"];
export const ManualPaymentStatusSchema = z.enum(MANUAL_PAYMENT_STATUSES);
export const ManualPaymentSummaryV1Schema = z
    .object({
    paymentId: z.string().min(1),
    status: ManualPaymentStatusSchema,
    amountCents: z.number().int().nonnegative().nullable(),
    currency: z.string().length(3),
    pilotReference: z.string().min(1).nullable(),
    pilotNote: z.string().min(1).nullable(),
    markedAt: z.string().datetime().nullable(),
})
    .strict();
/** Agency API view — includes payment summary, no internal scan blobs. */
export const AgencyFixKitViewV1Schema = z
    .object({
    schemaVersion: z.literal("agency-fix-kit-view.v1.0"),
    fixKitId: FixKitIdSchema,
    claimId: ClaimIdSchema,
    publicReportRef: ReportIdSchema,
    sku: z.literal(FIX_KIT_SKU_PILOT),
    generatedAt: z.string().datetime(),
    executiveSummary: z.string().min(1),
    items: z.array(FixKitItemV1Schema),
    nextStepProposal: z.string().min(1),
    disclaimers: z.array(z.string().min(1)),
    sourceScore: z.number().int().min(0).max(100),
    sourceScoreLabel: z.string().min(1),
    idempotentReplay: z.boolean(),
    payment: ManualPaymentSummaryV1Schema,
})
    .strict();
/** API response when Fix Kit composition is enqueued to the worker. */
export const FixKitGenerateAcceptedV1Schema = z
    .object({
    schemaVersion: z.literal("fix-kit-generate-accepted.v1.0"),
    status: z.literal("PENDING"),
    claimId: ClaimIdSchema,
})
    .strict();
export const ManualPaymentUpdateRequestV1Schema = z
    .object({
    schemaVersion: z.literal("manual-payment-update.v1.0"),
    status: ManualPaymentStatusSchema,
    amountCents: z.number().int().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    pilotReference: z.string().min(1).max(128).optional(),
    pilotNote: z.string().min(1).max(2000).optional(),
})
    .strict();
/** Client-safe printable view (sample + export). */
export const ClientSafeFixKitViewV1Schema = z
    .object({
    schemaVersion: z.literal("client-safe-fix-kit-view.v1.0"),
    title: z.string().min(1),
    executiveSummary: z.string().min(1),
    items: z.array(z
        .object({
        issueTitle: z.string().min(1),
        priority: FixKitPrioritySchema,
        recommendedFix: z.string().min(1),
        clientFacingLanguage: z.string().min(1),
    })
        .strict()),
    nextStepProposal: z.string().min(1),
    disclaimers: z.array(z.string().min(1)),
})
    .strict();
export const FIX_KIT_DISCLAIMERS = [
    "This Fix Kit is an automated pilot artifact based on a public AI Readiness scan. It is not legal, compliance, or performance advice.",
    "Payment tracking is manual for pilot purposes only — no card processing is performed by Enrein.",
];
//# sourceMappingURL=fix-kit.js.map